<template>
  <form class="form" @submit.prevent="handleSubmit(handleProceed)()">
    <div class="form-fields">
      <div class="mb-6 grid grid-cols-2 gap-6">
        <VFormInput
          label="Tag name*"
          placeholder="Enter tag name"
          :initialValue="initialValues.payout_tag_name"
          :error="errors.payout_tag_name"
          @update="value => handleSetField('payout_tag_name', value)"
        />

        <div>
          <div class="mb-2 -mt-6 text-xs font-semibold">Payout Type*</div>
          <div v-for="(type, index) in payoutTypes" :key="`payout-type-${index}`" class="ratio">
            <input
              class="ratio-input"
              :id="type.name"
              type="radio"
              :value="type.value"
              v-model="payoutType"
            />
            <label class="ratio-label" :for="type.name"></label>
            <span class="ratio-name">{{ type.name }}</span>
          </div>
        </div>
      </div>
      <div class="mb-6 grid grid-cols-3 gap-10">
        <div>
          <div class="mb-2 -mt-6 text-xs font-semibold">Machine Required*</div>
          <div
            v-for="(m, index) in machineRequiredTypes"
            :key="`embroidery-required-m-${index}`"
            class="pr-4 ratio"
          >
            <input
              class="ratio-input"
              :id="m.name"
              type="radio"
              :value="m.value"
              v-model="machineRequiredType"
            />
            <label class="ratio-label" :for="m.name"></label>
            <span class="ratio-name">{{ m.name }}</span>
          </div>
        </div>
        <div>
          <div class="mb-2 -mt-6 text-xs font-semibold">Required New Lead*</div>
          <div
            v-for="(m, index) in requiredNewLeadTypes"
            :key="`required-new-lead-m-${index}`"
            class="pr-4 ratio"
          >
            <input
              class="ratio-input"
              :id="m.name"
              type="radio"
              :value="m.value"
              v-model="requiredNewLeadType"
            />
            <label class="ratio-label" :for="m.name"></label>
            <span class="ratio-name">{{ m.name }}</span>
          </div>
        </div>
        <div>
          <div class="mb-2 -mt-6 text-xs font-semibold">Has DTG reward?*</div>
          <div
            v-for="(m, index) in hasDtgRewardTypes"
            :key="`has-dtg-reward-m-${index}`"
            class="pr-4 ratio"
          >
            <input
              class="ratio-input"
              :id="m.name"
              type="radio"
              :value="m.value"
              v-model="hasDtgRewardType"
            />
            <label class="ratio-label" :for="m.name"></label>
            <span class="ratio-name">{{ m.name }}</span>
          </div>
        </div>
      </div>

      <div class="mb-10">
        <div class="mb-4 text-xs font-semibold">
          Referral Threshold ( Referrer won't be credited for referred orders count below threshold
          )
        </div>
        <VFormInput
          placeholder="default is 0"
          label="Referral Threshold #"
          labelPosition="right"
          :onlyDigits="true"
          :initialValue="initialValues.minimum_referrals || 0"
          :error="errors.minimum_referrals"
          :classes="{ fieldContainer: 'mb-0', field: 'w-2/12 h-10' }"
          @update="value => handleSetField('minimum_referrals', value)"
        />
      </div>

      <div>
        <div class="mb-4 text-xs font-semibold">Product Type / Reward*</div>
        <!-- when applying percentage based payout show only one input by slicing machineTypes array and changing label -->
        <div class="grid grid-cols-2 gap-6">
          <VFormInput
            v-for="(machine, index) in isCurrency ? machineTypes : machineTypes.slice(0, 1)"
            :key="`machine-type-${index}`"
            :label="isCurrency ? `${machine.name}*` : `Entire order*`"
            placeholder=""
            labelPosition="right"
            :onlyDigits="true"
            :initialValue="handlePayoutInitialValue(machine.id, initialValues.payout_amounts)"
            :error="errors.payout_amounts"
            @update="value => handleUpdatePayout(machine.id, value)"
            :classes="{ fieldContainer: 'mb-0', field: 'w-3/12 h-10' }"
            :leftIcon="isCurrency"
            :rightIcon="!isCurrency"
          >
            <template #left>{{ getSymbol(payoutType) }}</template>
            <template #right>{{ getSymbol(payoutType) }}</template>
          </VFormInput>
          <VFormInput
            placeholder=""
            label="Shop Products Reward"
            labelPosition="right"
            :onlyDigits="true"
            :initialValue="initialValues.shop_products_reward || 0"
            :error="errors.shop_products_reward"
            @update="value => handleSetField('shop_products_reward', value)"
            :classes="{ fieldContainer: 'mb-0', field: 'w-3/12 h-10' }"
            :leftIcon="isCurrency"
            :rightIcon="!isCurrency"
          >
            <template #left>{{ getSymbol(payoutType) }}</template>
            <template #right>{{ getSymbol(payoutType) }}</template>
          </VFormInput>
        </div>
      </div>
    </div>
    <div class="form-buttons">
      <button class="form-buttons-cancel" type="button" @click="() => $emit('close')">
        Cancel
      </button>
      <button type="submit" class="form-buttons-proceed" :disabled="formLoading">
        <BeatLoader
          v-if="formLoading"
          color="#FAFAFA"
          :size="0.875"
          sizeUnit="rem"
          class="flex items-center"
        />
        <span v-else>Add</span>
      </button>
    </div>
  </form>
</template>

<script>
import VFormInput from '@/components/base/VFormInput';
import { formMixin } from '@/mixins';

const PAYOUT_TYPES = [
  // NOTE: DISABLE CURRENCY TYPE UNTIL THEY FIGURE OUT A WAY TO HANDLE THIS WITH NETSUITE
  // { name: 'Dollar amount', symbol: '$', value: 'currency' },
  { name: 'Percentage', symbol: '%', value: 'percentage' },
];

const MACHINE_REQUIRED_TYPES = [
  { name: 'Yes', value: 1 },
  { name: 'No', value: 0 },
];

const REQUIRED_NEW_LEAD_TYPES = [
  { name: 'Required', value: 1 },
  { name: 'No Required', value: 0 },
];

const HAS_DTG_REWARD_TYPES = [
  { name: 'Has', value: 1 },
  { name: 'Does not', value: 0 },
];

export default {
  name: 'PayoutTagForm',
  components: { VFormInput },
  props: {
    machineTypes: {
      type: Array,
      required: false,
      default: () => [],
    },
    editable: {
      type: Boolean,
      required: false,
      default: false,
    },
    editableTag: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    initialValues: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    payoutTags: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  mixins: [
    formMixin(
      {
        payout_tag_name: '',
        payout_type: '',
        payout_amounts: [],
        minimum_referrals: 0,
        shop_products_reward: 0,
        machine_required: '',
        required_new_lead: '',
        has_dtg_reward: '',
      },
      [
        'minimum_referrals',
        'shop_products_reward',
        'machine_required',
        'required_new_lead',
        'has_dtg_reward',
      ],
    ),
  ],
  data() {
    return {
      payoutTypes: PAYOUT_TYPES,
      payoutType: '',
      machineRequiredTypes: MACHINE_REQUIRED_TYPES,
      machineRequiredType: '',
      requiredNewLeadTypes: REQUIRED_NEW_LEAD_TYPES,
      requiredNewLeadType: '',
      hasDtgRewardTypes: HAS_DTG_REWARD_TYPES,
      hasDtgRewardType: '',
    };
  },
  watch: {
    payoutType(type) {
      this.handleSetField('payout_type', type);
    },
    machineRequiredType(machineRequired) {
      this.handleSetField('machine_required', machineRequired);
    },
    requiredNewLeadType(requiredNewLead) {
      this.handleSetField('required_new_lead', requiredNewLead);
    },
    hasDtgRewardType(hasDtgReward) {
      this.handleSetField('has_dtg_reward', hasDtgReward);
    },
  },
  computed: {
    isCurrency() {
      return this.payoutType === 'currency';
    },
  },
  methods: {
    getSymbol(value) {
      return this.payoutTypes.find(pt => pt.value === value)['symbol'];
    },
    handleUpdatePayout(machineTypeId, value) {
      this.handleSetField(
        'payout_amounts',
        { machine_type_id: machineTypeId, value },
        'machine_type_id',
      );
    },
    handlePayoutInitialValue(machineTypeId, initialPayoutValues) {
      const payout =
        initialPayoutValues && initialPayoutValues.find(p => p.machine_type_id === machineTypeId);
      return payout && payout.value;
    },
    async handleProceed(data) {
      this.editable ? await this.handleUpdate(data) : await this.handleAdd(data);
      this.handleResetFields();
      this.handleResetErrors();
      this.$emit('close');
    },
    async handleUpdate(data) {
      const { payout_amounts, payout_type } = data;
      const payoutData = this.prepPayoutAmountsData(payout_amounts, payout_type);
      const tagId = await this.handleUpdateTag(data);
      await this.$store.dispatch('admin/updatePayoutTag', { tagId, data: payoutData });
    },
    async handleUpdateTag(data) {
      const { id } = this.editableTag;
      const {
        payout_tag_name: name,
        payout_type: type,
        minimum_referrals,
        shop_products_reward,
        machine_required,
        required_new_lead,
        has_dtg_reward,
      } = data;
      const res = await this.$http.post(`/admin/referral-payout-tag/${id}`, {
        name,
        type,
        minimum_referrals,
        shop_products_reward,
        machine_required,
        required_new_lead,
        has_dtg_reward,
      });
      return res.data.data.id;
    },
    async handleAddTag(data) {
      const {
        payout_tag_name: name,
        payout_type: type,
        minimum_referrals,
        shop_products_reward,
        machine_required,
        required_new_lead,
        has_dtg_reward,
      } = data;
      const res = await this.$http.post(`/admin/referral-payout-tag`, {
        name,
        type,
        minimum_referrals,
        shop_products_reward,
        machine_required,
        required_new_lead,
        has_dtg_reward,
      });
      return res.data.data.id;
    },
    async handleAddPayout(tagId, data) {
      const { payout_amounts, payout_type } = data;
      const payoutData = this.prepPayoutAmountsData(payout_amounts, payout_type);
      await this.$store.dispatch('admin/addPayoutTag', { tagId, data: payoutData });
    },
    async handleAdd(data) {
      const tagId = await this.handleAddTag(data);
      await this.handleAddPayout(tagId, data);
    },
    async handleDeleteTag(id) {
      try {
        await this.$store.dispatch('admin/deletePayoutTag', id);
      } catch (error) {
        return this.$notify({
          group: 'error',
          type: 'error',
          title: error.response.statusText,
          text: error.response.data.message,
        });
      }
    },
    prepPayoutAmountsData(payoutAmounts, payoutType) {
      if (payoutAmounts.length !== this.machineTypes.length) {
        return payoutType === 'currency'
          ? this.prepPayoutAmountsDataCurrency(payoutAmounts, this.machineTypes)
          : this.prepPayoutAmountsDataPercentage(payoutAmounts, this.machineTypes);
      }
      return payoutAmounts;
    },
    // set default values to 0 for unset payout amount when payout type is currency.
    prepPayoutAmountsDataCurrency(data, machineTypes) {
      return machineTypes.map(m => {
        const match = data.find(p => p.machine_type_id === m.id);
        return match ? match : { machine_type_id: m.id, value: 0 };
      });
    },
    // apply same percentage value to all machines when payout type is percentage
    prepPayoutAmountsDataPercentage(data, machineTypes) {
      const first = data[0];
      return machineTypes.map(m => ({ machine_type_id: m.id, value: first.value }));
    },
  },
  created() {
    this.payoutType = this.initialValues.payout_type || 'percentage';
    this.machineRequiredType =
      this.initialValues.machine_required == undefined ? '1' : this.initialValues.machine_required;
    this.requiredNewLeadType =
      this.initialValues.required_new_lead == undefined
        ? '1'
        : this.initialValues.required_new_lead;
    this.hasDtgRewardType =
      this.initialValues.has_dtg_reward == undefined ? '1' : this.initialValues.has_dtg_reward;
  },
};
</script>
